import { Injectable } from '@angular/core';
import { EntityCard } from '~entity/entity-card/entity-card.model';
import { EntityIndicador } from '~entity/entity-indicador/entity-indicador.model';
import { CardItemVo } from '~model/vo/card-item-vo';

export interface ProcessedCardItem extends CardItemVo {
  style: { [key: string]: any };
  secUnit: boolean;
  formattedPrincipal: string;
  formattedSecundario: string;
  digitInfo: string;
  digitInfoSecundario: string;
}

export interface ProcessedCardData extends EntityCard {
  vItem: ProcessedCardItem[];
}

@Injectable({
  providedIn: 'root'
})
export class CardResumoDataService {

  private corBad: string = '#d12828';
  private corNeutral: string = '#257eca';
  private corGood: string = '#278134';

  public digitDecimal: string = '1.2-2';
  public digitInteger: string = '1.0-0';

  constructor() { }

  /**
   * Processa os dados do card resumo aplicando estilos, cores e formatação
   * @param cardData - Dados do card
   * @param indicador - Indicador associado ao card
   * @returns Dados processados prontos para exibição
   */
  processCardData(cardData: EntityCard, indicador: EntityIndicador): ProcessedCardData {
    const processedItems: ProcessedCardItem[] = cardData.itens.map((item, idx) => {
      const processedItem: ProcessedCardItem = {
        ...item,
        secUnit: this.showSecundaryUnit(item, idx, cardData),
        style: item.indicador ? { color: this.getCor(item.principal, item.tipo, idx, cardData, indicador) } : {},
        formattedPrincipal: this.formatValue(item.principal, item.tipo),
        formattedSecundario: this.formatValue(item.secundario, item.tipoSecundario),
        digitInfo: this.getDigitInfo(item.tipo),
        digitInfoSecundario: this.getDigitInfo(item.tipoSecundario)
      };

      return processedItem;
    });

    return {
      ...cardData,
      vItem: processedItems
    };
  }

  /**
   * Formata um valor de acordo com seu tipo
   * @param value - Valor a ser formatado
   * @param tipo - Tipo do valor
   * @returns Valor formatado como string
   */
  formatValue(value: string | number | undefined, tipo?: string): string {
    if (!value || value === '-') {
      return value?.toString() || '';
    }

    if (tipo === 'DECIMAL' || tipo === 'PERCENTUAL') {
      return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  /**
   * Obtém a informação de dígitos para formatação
   * @param tipo - Tipo do valor
   * @returns String de formatação de dígitos
   */
  getDigitInfo(tipo?: string): string {
    if (tipo === 'DECIMAL' || tipo === 'PERCENTUAL') {
      return this.digitDecimal;
    }
    return this.digitInteger;
  }

  /**
   * Calcula a cor baseada no valor, tipo e indicador
   * @param valor - Valor do item
   * @param tipo - Tipo do valor
   * @param index - Índice do item
   * @param cardData - Dados do card
   * @param indicador - Indicador associado
   * @returns Cor em formato string
   */
  getCor(
    valor: string | number | undefined, 
    tipo: string | undefined, 
    index: number,
    cardData: EntityCard,
    indicador: EntityIndicador
  ): string {
    const base = cardData.itens[index].base || 1;

    const naN = isNaN(Number(valor));
    const val = (naN ? 0 : Number(valor));
    const porcentagem: number = (tipo === 'PERC_INT' || tipo === 'PERCENTUAL') ?
      val : (val / base) * 100;

    if (naN) {
      return 'inherit';
    }
    if (porcentagem >= indicador.valorMaximo) {
      return (indicador.reverso) ? this.corBad : this.corGood;
    } else if (porcentagem >= indicador.valorMinimo) {
      return this.corNeutral;
    } else {
      return (indicador.reverso) ? this.corGood : this.corBad;
    }
  }

  /**
   * Determina se deve mostrar unidade secundária
   * @param item - Item do card
   * @param index - Índice do item
   * @param cardData - Dados do card
   * @returns Boolean indicando se deve mostrar unidade secundária
   */
  showSecundaryUnit(item: CardItemVo, index: number, cardData: EntityCard): boolean {
    return !!(cardData.id !== 'pedidos' || (index === 0 || cardData.config.exibirPercentuais));
  }

  /**
   * Extrai apenas os dados essenciais do card sem processamento visual
   * @param cardData - Dados do card
   * @returns Dados básicos do card
   */
  extractBasicData(cardData: EntityCard): {
    id: string;
    descricao: string;
    itens: {
      descricao: string;
      principal: string | number;
      unidade?: string;
      secundario?: string | number;
      unidadeSecundario?: string;
    }[];
  } {
    return {
      id: cardData.id,
      descricao: cardData.descricao,
      itens: cardData.itens.map(item => ({
        descricao: item.descricao,
        principal: item.principal,
        unidade: item.unidade,
        secundario: item.secundario,
        unidadeSecundario: item.unidadeSecundario
      }))
    };
  }

  /**
   * Obtém apenas valores numéricos do card
   * @param cardData - Dados do card
   * @returns Array com valores numéricos
   */
  extractNumericValues(cardData: EntityCard): {
    id: string;
    descricao: string;
    valores: {
      descricao: string;
      valorPrincipal: number;
      valorSecundario?: number;
    }[];
  } {
    return {
      id: cardData.id,
      descricao: cardData.descricao,
      valores: cardData.itens.map(item => ({
        descricao: item.descricao,
        valorPrincipal: typeof item.principal === 'number' ? item.principal : parseFloat(item.principal.toString()) || 0,
        valorSecundario: item.secundario ? 
          (typeof item.secundario === 'number' ? item.secundario : parseFloat(item.secundario.toString())) : 
          undefined
      }))
    };
  }
}
